// The trade settings editor of an Options trade card (moved verbatim from pages/options/OptionsHedge.js so the
// BUY and SELL OPTIONS cards share it). `fields` defaults to the BUY card's list; the SELL card omits Spread to
// Maintain (a BUY hedge concept). Only CHANGED fields are sent to PUT /options-hedge/trades/{id}/settings.
import React, { useState } from "react";
import { px } from "@/components/options/OptionsCommon";
import { targetPercent, targetRupees } from "@/lib/targetValues";

const rs = (v) => (v === null || v === undefined ? "—" : `₹${px(v)}`);
const btn = "px-2 py-1 rounded-sm text-xs font-bold border";
export const EXIT_FIELDS = [["profit_target", "Target ₹"], ["profit_target_pct", "Target %"], ["min_opportunity", "Spread to Maintain"],
  ["stop_loss", "Stop loss ₹"], ["exit_before_expiry_days", "Exit days before expiry"], ["exit_time_ist", "Exit time IST"], ["exit_wait_s", "Exit wait s"]];
export const SELL_EXIT_FIELDS = EXIT_FIELDS.filter(([k]) => k !== "min_opportunity");
export const NULLABLE_EDIT = ["profit_target", "profit_target_pct", "stop_loss"];

/** Trade settings editor (opened from the card's Target / Spread cells, like the Futures card). Target
 *  Rs and % are linked on the trade's Allot Funds with the Futures helpers (lib/targetValues.js); only the
 *  CHANGED fields are sent. Spread to Maintain applies to pairs not yet sent. */
export default function ExitSettings({ t, onSave, fields = EXIT_FIELDS, note }) {
  const init = () => {
    const v = Object.fromEntries(fields.map(([k]) => [k, t.settings?.[k] ?? ""]));
    // a trade created before Target % existed: show its % derived from Rs on Allot Funds (as the card does)
    if (v.profit_target_pct === "" && v.profit_target !== "") v.profit_target_pct = targetPercent(t.allocated_fund, v.profit_target);
    return v;
  };
  const [f, setF] = useState(init);
  const [orig] = useState(init);
  const alloc = t.allocated_fund;
  const set = (k, v) => {
    if (k === "profit_target") setF({ ...f, profit_target: v, profit_target_pct: (v === "" || Number(v) >= 0) ? targetPercent(alloc, v) : f.profit_target_pct });
    else if (k === "profit_target_pct") setF({ ...f, profit_target_pct: v, profit_target: targetRupees(alloc, v) });
    else setF({ ...f, [k]: v });
  };
  const body = () => {
    const changed = fields.map(([k]) => k).filter((k) => String(f[k] ?? "") !== String(orig[k] ?? ""));
    // a target edit sends the field the user typed in; the backend derives the other
    const tgt = changed.includes("profit_target_pct") && !changed.includes("profit_target") ? "profit_target_pct" : "profit_target";
    return Object.fromEntries(changed.filter((k) => !["profit_target", "profit_target_pct"].includes(k) || k === tgt)
      .map((k) => [k, k === "exit_time_ist" ? f[k] : (f[k] === "" || f[k] === null ? (NULLABLE_EDIT.includes(k) ? null : undefined) : Number(f[k]))])
      .filter(([, v]) => v !== undefined));
  };
  return (
    <div className="term-panel-2 rounded-sm p-2 flex flex-wrap items-center gap-2 text-xs" data-testid="hedge-exit-settings">
      <b>Trade settings</b>
      {fields.map(([k, label]) => (
        <label key={k} className="flex items-center gap-1"><span className="text-[var(--term-muted)]">{label}</span>
          <input className="bg-[var(--term-input)] border border-[var(--term-border-strong)] rounded-sm px-1.5 py-0.5 w-20 mono" value={f[k] ?? ""}
            placeholder={NULLABLE_EDIT.includes(k) ? "off" : ""} onChange={(e) => set(k, e.target.value)} data-testid={`hedge-exit-${k}`} /></label>))}
      <button className={`${btn} border-[#2563EB] text-[#2563EB]`} onClick={() => onSave(body())} data-testid="hedge-exit-settings-save">Save</button>
      <span className="text-[10px] text-[var(--term-muted)]">{note ?? `Target % is of Allot Funds (${rs(alloc)}). Spread to Maintain applies to pairs not yet sent - filled, working and recovering pairs keep theirs.`}</span>
    </div>
  );
}
